import React, { useEffect, useState } from 'react';
import { Button, Flex, FlexProps, Text } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';

import { BulletinTag } from '../../Utils/BulletinTag';
import { GenericTable } from '../Common/GenericTable';
import { TablePager } from '../Common/TablePager';
import arrowIcon from '../../Resources/Images/Icons/arrow_icon.svg';

export interface BulletinManagerItem {
  bid: number;
  date: string;
  title: string;
  tag: BulletinTag;
}

interface Props extends Pick<FlexProps, 'mt'> {
  bulletins: BulletinManagerItem[];
  sortDirection: 'asc' | 'desc';
  onSortToggle: () => void;
}

const PAGE_SIZE_OPTIONS = [10, 20] as const;

const TABLE_CELL_TEXT_STYLE = {
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.01em',
  color: '#00292B',
};

const TAG_STYLES: Record<BulletinTag, { bg: string; width: string; borderRadius: string }> = {
  系統維護: { bg: '#99DDFF', width: '93px', borderRadius: '5px' },
  最新消息: { bg: '#D3B910', width: '93px', borderRadius: '5px' },
  新功能上線: { bg: '#00AAFF', width: '111px', borderRadius: '5px' },
};

const formatDisplayDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const normalized = dateStr.replace(/\./g, '/').replace(/-/g, '/').trim();
  const parts = normalized.split('/').map((p) => p.trim());
  if (parts.length >= 3) {
    const [year, month, day] = parts;
    return `${Number(year)}/${Number(month)}/${Number(day)}`;
  }
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return dateStr;
  return `${parsed.getFullYear()}/${parsed.getMonth() + 1}/${parsed.getDate()}`;
};

const BulletinTagBadge: React.FC<{ tag: BulletinTag }> = ({ tag }) => {
  const style = TAG_STYLES[tag];
  return (
    <Flex
      justify="center"
      align="center"
      h="31px"
      minW={style.width}
      px="10px"
      py="2px"
      borderRadius={style.borderRadius}
      bg={style.bg}
    >
      <Text {...TABLE_CELL_TEXT_STYLE} fontSize="18px" whiteSpace="nowrap">
        {tag}
      </Text>
    </Flex>
  );
};

export const BulletinManagerTable: React.FC<Props> = ({ bulletins, sortDirection, onSortToggle, mt }) => {
  const history = useHistory();
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const totalItems = bulletins.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = totalItems === 0 ? 0 : (safePage - 1) * pageSize;
  const paginated = bulletins.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [bulletins]);

  const columns = [
    { label: '主題', width: '200px', paddingLeft: '40px' },
    {
      label: '日期',
      width: '230px',
      sortDirection,
      onSort: onSortToggle,
      sortArrowSrc: arrowIcon,
      sortArrowAlt: '排序',
    },
    { label: '標題', width: '510px' },
    { label: '', width: '100px' },
  ];

  const renderRow = (item: BulletinManagerItem) => (
    <Flex key={item.bid} w="1040px" minH="60px" align="stretch">
      <Flex w="200px" minW="200px" justify="flex-start" align="center" px="40px" borderBottom="1px solid #00AAFF">
        <BulletinTagBadge tag={item.tag} />
      </Flex>

      <Flex w="230px" minW="230px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE}>{formatDisplayDate(item.date)}</Text>
      </Flex>

      <Flex w="510px" minW="510px" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Text {...TABLE_CELL_TEXT_STYLE} noOfLines={1} title={item.title}>
          {item.title}
        </Text>
      </Flex>

      <Flex w="100px" minW="100px" justify="center" align="center" px="12px" borderBottom="1px solid #00AAFF">
        <Button
          variant="unstyled"
          minW="37px"
          h="22px"
          onClick={() => history.push('/bs/bulletin/edit', { bid: item.bid })}
        >
          <Text
            fontFamily="'Open Sans', sans-serif"
            fontSize="18px"
            fontWeight={600}
            lineHeight="120%"
            letterSpacing="0.01em"
            textDecoration="underline"
            color="#006699"
          >
            編輯
          </Text>
        </Button>
      </Flex>
    </Flex>
  );

  return (
    <Flex direction="column" w="1040px" mt={mt}>
      <GenericTable columns={columns} isEmpty={paginated.length === 0} emptyText="目前沒有公告">
        {paginated.map(renderRow)}
      </GenericTable>

      <TablePager
        mt="50px"
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={safePage}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setCurrentPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setCurrentPage(1);
        }}
        labels={{
          show: '顯示',
          itemsUnit: '筆',
          range: (s, e) => `${s} - ${e} 筆`,
          prevPage: '上一頁',
          nextPage: '下一頁',
        }}
      />
    </Flex>
  );
};

export default BulletinManagerTable;
